import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';

interface ErrorLog {
  timestamp: string;
  type: 'error' | 'console';
  level?: 'log' | 'warn' | 'error' | 'info';
  message: string;
  stack?: string;
  url?: string;
  line?: number;
  column?: number;
  userAgent: string;
  screenResolution: string;
}

interface ErrorLoggerContextType {
  getErrorLogs: () => ErrorLog[];
  clearErrorLogs: () => void;
}

const ErrorLoggerContext = createContext<ErrorLoggerContextType | undefined>(undefined);

export const useErrorLogger = () => {
  const context = useContext(ErrorLoggerContext);
  if (!context) {
    throw new Error('useErrorLogger must be used within ErrorLoggerProvider');
  }
  return context;
};

interface ErrorLoggerProviderProps {
  children: ReactNode;
}

export const ErrorLoggerProvider: React.FC<ErrorLoggerProviderProps> = ({ children }) => {
  const errorLogsRef = useRef<ErrorLog[]>([]);
  const maxLogs = 50; // Keep last 50 logs

  const addLog = (log: ErrorLog) => {
    errorLogsRef.current.push(log);
    if (errorLogsRef.current.length > maxLogs) {
      errorLogsRef.current.shift();
    }
  };

  const getErrorLogs = () => [...errorLogsRef.current];

  const clearErrorLogs = () => {
    errorLogsRef.current = [];
  };

  useEffect(() => {
    // Capture JavaScript errors
    const errorHandler = (event: ErrorEvent) => {
      const log: ErrorLog = {
        timestamp: new Date().toISOString(),
        type: 'error',
        message: event.message,
        stack: event.error?.stack,
        url: event.filename,
        line: event.lineno,
        column: event.colno,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
      };
      addLog(log);
    };

    // Capture unhandled promise rejections
    const rejectionHandler = (event: PromiseRejectionEvent) => {
      const log: ErrorLog = {
        timestamp: new Date().toISOString(),
        type: 'error',
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
      };
      addLog(log);
    };

    // Override console methods to capture logs
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
    };

    const createConsoleOverride = (level: 'log' | 'warn' | 'error' | 'info') => {
      return (...args: any[]) => {
        const message = args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ');

        const log: ErrorLog = {
          timestamp: new Date().toISOString(),
          type: 'console',
          level,
          message,
          userAgent: navigator.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
        };
        addLog(log);

        // Call original console method
        originalConsole[level](...args);
      };
    };

    console.log = createConsoleOverride('log');
    console.warn = createConsoleOverride('warn');
    console.error = createConsoleOverride('error');
    console.info = createConsoleOverride('info');

    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', rejectionHandler);

    return () => {
      // Restore original console methods
      console.log = originalConsole.log;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
      console.info = originalConsole.info;

      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', rejectionHandler);
    };
  }, []);

  return (
    <ErrorLoggerContext.Provider value={{ getErrorLogs, clearErrorLogs }}>
      {children}
    </ErrorLoggerContext.Provider>
  );
};
